<template>
  <component
    :is="component"
    draggable="false"
    :to="to ?? { name: 'app-groups-slug', params: { slug: group.slug }, query: { ...(token ? { groupToken: token } : {}) }}"
    :style="{ backgroundImage: `url(${group.banner?.getSize('s') ? group.banner.getSize('s') : bannerPlaceholder})` }"
    class="text-left bg-cover bg-center bg-light-100 dark:bg-dark-800 overflow-hidden text-white rounded-2xl block" ref="$container"
  >
    <div class="bg-gradient-to-tr from-dark-950/90 via-dark-950/80 to-dark-950/70 min-h-[4.5rem] p-3">
      <div class="flex items-center space-x-2.5">
        <!-- <div class="size-10 shrink-0 bg-cover rounded-lg relative" :style="{ backgroundImage: `url(${group.banner?.getSize('s') ? group.banner.getSize('s') : bannerPlaceholder})` }" /> -->

        <div class="grow">
          <p class="font-bold text-base break-all line-clamp-1 leading-tight">{{ group.name }}</p>
          
          <div class="flex items-center space-x-1 mt-0.5">
            <div class="bg-ruby-200 text-ruby-800 text-5xs text-shadow-none font-black inline-flex items-center space-x-0.5 pr-1.5 pl-1 py-0.5 rounded-full uppercase" v-if="group.isSpicy">
              <IconBase name="cute/fire-fill" :width="11" />
              <span>{{ $t('components.Group.spicy') }}</span>
            </div>
            <div class="bg-royal-200 text-royal-800 text-5xs text-shadow-none font-black inline-flex items-center space-x-0.5 pr-1.5 pl-1 py-0.5 rounded-full uppercase" v-if="group.isOfficial">
              <IconBase name="cute/verified-fill" :width="11" />
              <span>{{ $t('components.Group.official') }}</span>
            </div>

            <p class="font-medium text-2xs mt-0.5 break-all line-clamp-1 leading-tight opacity-60">
              {{ toK(group.membersCount ?? 0) }} {{ $t('components.Group.memberCount') }}
            </p>
          </div>
        </div>

        <ButtonBase color="weak" class="shrink-0 pointer-events-none" size="2xs" v-if="group.isPending(currentUser?._id)">
          <span class="px-1">{{ $t('common.pending') }}</span>
        </ButtonBase>
        <ButtonBase @click.prevent="() => groupStore.join(group?._id ?? '')" :color="group.application !== 'open' ? 'white' : 'builders'" class="shrink-0" size="2xs" v-else-if="!group.isMember(currentUser?._id)">
          <span class="px-1">
            <IconBase name="cute/lock-fill" :width="12" class="align-baseline -mt-0.5" v-if="group.application !== 'open'" />

            {{ $t('actions.join') }}
          </span>
        </ButtonBase>
        <ButtonBase @click.prevent="() => groupStore.leave(group?._id ?? '')" color="white" class="shrink-0" size="2xs" v-else>
          <span class="px-1">{{ $t('actions.joined') }}</span>
        </ButtonBase>
      </div>

      <div class="line-clamp-2 leading-tight mt-2 text-sm" v-if="group.description && isDescription">
        {{ stripTags(group.description) }}
      </div>
      
      <slot />
    </div>
  </component>
</template>

<script lang="ts" setup>
import { Group } from '@/classes'
import UserStore from '@/store/user'
import GroupStore from '@/store/groups'
import SentimentsStore from '@/store/sentiments'
import bannerPlaceholder from '@/assets/img/gradients/hard/gradient-6.jpg'

const props = defineProps<{
  group: Group,
  to?: RouteLocationRaw
  token?: string
  isDescription?: boolean
  isNoLink?: boolean
}>()

const route = useRoute()
const groupStore = GroupStore()
const sentimentsStore = SentimentsStore()

const $container = ref()
const { stop } = useIntersectionObserver($container,
  ([entry]) => {
    if (entry?.isIntersecting) {
      sentimentsStore.skip({
        groupId: props.group._id,
      })
      
      stop()
    }
  },
  {
    threshold: 0.8
  }
)

const userStore = UserStore()
const { currentUser } = storeToRefs(userStore)

const component = computed(() => props.isNoLink ? 'div' : resolveComponent('NuxtLink'))

</script>